import React, { useMemo } from "react";
import classNames from "classnames";
import { format } from "date-fns";

import FeaturedMatch from "../FeaturedMatch/FeaturedMatch";
import { StyledFeaturedMatch } from "../FeaturedMatch/StyledFeaturedMatch";
import { useContainerQuery } from "react-container-query";
import {
  featuredMatchCustomQueries,
  getClubSportecId,
  isD3Column9,
  isFeaturedMatchXxs,
} from "../../utils";
import useFeaturedMatchMLSMatches from "../../hooks/useFeaturedMatchMLSMatches";
import useForgeMatches from "../../../mls-match-list/hooks/useForgeMatches";
import useMlsSeasons from "../../../mls-match-list/hooks/useMlsSeasons";
import useMLSMatch from "../../../mls-match-list/hooks/useMLSMatch";
import { parseDateWithBeforeAndAfter } from "../../../shared/containers/ReactApp/MatchHandler";
import FeaturedMatchLoader from "../../../mls-featured-match/loader";
import EmptyState from "../../../shared/components/EmptyState";

function FeaturedMatchWrapper({ options }) {
  const [params, containerRef] = useContainerQuery(featuredMatchCustomQueries);
  const {
    matchSportecId,
    previousMatchesCount,
    nextMatchesCount,
    clubSportecId: initialClubSportecId,
    competitionSportecId,
    compactVersion,
    showClubRanks,
    showCompetitionStage,
    showRoundDetail,
    showGroupInformation,
  } = options || {};
  const matchDate = useMemo(() => {
    const { originalDate } = options?.matchDate
      ? parseDateWithBeforeAndAfter(options?.matchDate)
      : { originalDate: null };

    return originalDate;
  }, [options.matchDate]);

  const clubSportecId = initialClubSportecId || getClubSportecId();

  const { data: mlsMatch, isLoading: isMlsMatchLoading } = useMLSMatch({
    matchSportecId,
    options: {
      queryKey: ["mls-featured-match", matchSportecId],
      enabled: !!matchSportecId,
    },
  });

  const { data: seasons, isLoading: isSeasonsLoading } = useMlsSeasons({
    competitionSportecId,
  });

  const seasonSportecId = useMemo(() => {
    const year = format(new Date(), "yyyy");
    const season = seasons?.get(Number(year));

    return season?.season_id;
  }, [seasons]);

  const {
    data: mlsMatches,
    isLoading: isMlsMatchesLoading,
    isError: isMlsMatchesError,
    isFetching: isMlsMatchesFetching,
  } = useFeaturedMatchMLSMatches({
    matchDate: mlsMatch?.match_information?.kickoff_time || matchDate,
    seasonSportecId:
      mlsMatch?.match_information?.season_id ||
      seasonSportecId ||
      window?.forgeVariables?.currentSite?.currentSeasonSportecId,
    matchSportecId,
    dateFrom: options?.dateFrom,
    dateTo: options?.dateTo,
    competitionSportecId,
    clubSportecId,
    previousMatchesCount: Number(previousMatchesCount),
    nextMatchesCount: Number(nextMatchesCount),
  });

  const { previousMatches = [], featuredMatch, nextMatches = [] } =
    mlsMatches || {};

  const {
    data: forgeMatches,
    isLoading: isForgeMatchesLoading,
  } = useForgeMatches({
    mlsMatches: isMlsMatchesLoading
      ? []
      : [...previousMatches, featuredMatch, ...nextMatches],
  });

  if (
    isMlsMatchLoading ||
    isMlsMatchesLoading ||
    isForgeMatchesLoading ||
    isSeasonsLoading ||
    isMlsMatchesFetching
  ) {
    return <FeaturedMatchLoader />;
  }

  if (!featuredMatch) {
    return <EmptyState emptyStateTranslation="match_no_matches" />;
  }

  const isSmallContainer = isD3Column9(params);
  const isExtraSmallContainer = isFeaturedMatchXxs(params);

  return (
    <StyledFeaturedMatch
      className={`mls-c-featured-match__wrapper ${classNames(params)} ${
        options.compactVersion ? "--compact" : ""
      }`}
      ref={containerRef}
    >
      <FeaturedMatch
        featuredMatch={featuredMatch}
        featuredForgeMatch={forgeMatches?.get(featuredMatch?.match_id)}
        forgeMatches={forgeMatches}
        showClubRanks={showClubRanks}
        showCompetitionStage={showCompetitionStage}
        showRoundDetail={showRoundDetail}
        showGroupInformation={showGroupInformation}
        hideBlockheader={options?.hideBlockheader}
        blockheaderTitle={options?.blockheaderTitle}
        blockheaderSubtitle={options?.blockheaderSubtitle}
        previousMatches={previousMatches}
        nextMatches={nextMatches}
        queryClassName={classNames(params)}
        compactVersion={compactVersion}
        isSmallContainer={isSmallContainer}
        isExtraSmallContainer={isExtraSmallContainer}
      />
    </StyledFeaturedMatch>
  );
}

FeaturedMatchWrapper = React.memo(FeaturedMatchWrapper);

export default FeaturedMatchWrapper;
